import React, { useCallback, useEffect, useState } from "react";
import {
  Content,
  ContentType,
  Lesson,
  ExtendedUnit,
  ExtendedHeaUnit,
} from "@types";
import { isHomeEDApp } from "@constants";
import styles from "./selectedContent.module.scss";
import FullScreenWrapper from "../full-screen-wrapper/FullScreenWrapper";
import Video from "./video/Video";
import Audio from "./audio/Audio";
import Slide from "./slide/Slide";
// import GoogleSlide from './google-slide/GoogleSlide';
import Form from "./form/Form";
import Pdf from "./pdf/Pdf";
import Skill from "./skill/Skill";
import Parent from "./parent/Parent";
import DefaultContent from "./Default";
import { useNextContent } from "../useNextContent";
import preview from "../../../assets/img/preview.svg?url";
import { useCourseContent } from "@hooks/useCourseContent";
import {
  useUpdateLastVisitedLessonMutation,
  LastVisitedLessonContentFragmentDoc,
  useUpdateLastVisitedUnitStudyReviewMutation,
  LastVisitedUnitStudyReviewContentFragmentDoc,
} from "@api/graphql-codegen";
import { useIntercom } from "react-use-intercom";
import MaterialPDF from "@src/components/material-pdf/MaterialPDF";
import { useActivityTracker } from "@src/hooks/useActivity";
import RevealSlideView from "./revealslide/RevealSlide";
import ZipRevealSlideView from "./revealslide/ZipRevealSlide";
import { logger } from '../../../utils/logger';
// import Button from '@src/components/button/Button';

type Props = {
  content: Content;
  lesson?: Lesson;
  lessons?: Lesson[];
  unit?: ExtendedUnit | ExtendedHeaUnit;
  goBack?: () => void;
  onExit?: () => void;
  isPreview?: boolean;
  showBanner?: boolean; // Preview banner
  lastPageNum?: number;
  isParentApp?: boolean;
  studyPlanId?: string;
  isMaterial?: boolean;
  needPrint?: boolean;
  startLesson?: (lesson: Lesson) => void;
};

function SelectedContent(props: Props) {
  const {
    content,
    lesson,
    goBack,
    onExit,
    isPreview = false,
    showBanner = false,
    isMaterial = false,
    needPrint = false,
    lastPageNum,
    isParentApp,
    studyPlanId,
    unit,
    startLesson,
  } = props;
  const { hasNextContent, nextContent, selectNext } = useNextContent();
  const {
    currentUnit,
    program,
    pageNumber,
    setPageNumber,
    setIsPreview,
    section,
    isStudyGuide,
  } = useCourseContent();
  const [curContent, setCurContent] = useState<Content>();
  const [timer, setTimer] = useState<number>(30 * 60); // 30 minutes in seconds
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { update } = useIntercom();
  const { pauseActivityTracker, resumeActivityTracker } = useActivityTracker();

  useEffect(() => {
    update?.({
      hideDefaultLauncher: true,
    });
    if (isPreview) {
      setIsPreview(true);
    }
    // return () => update?.({
    //   'hideDefaultLauncher': false
    // })
  }, [isPreview, setIsPreview, update]);

  useEffect(() => {
    if (lastPageNum) {
      setPageNumber(lastPageNum);
    }
  }, [lastPageNum, setPageNumber]);

  useEffect(() => {
    if (!isPreview) {
      // Pause activity tracker for first 30 minutes and then resume
      pauseActivityTracker();
    }

    // Resume activity tracker when a teacher ends a lesson
    return () => resumeActivityTracker();
  }, [isPreview, pauseActivityTracker, resumeActivityTracker]);

  useEffect(() => {
    if (timer > 0) {
      const timerId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timerId); // Clear the interval on component unmount
    }
  }, [timer]);

  useEffect(() => {
    if (timer === 0) {
      resumeActivityTracker();
    }
  }, [resumeActivityTracker, timer]);

  const [updateLastVisitedLesson] = useUpdateLastVisitedLessonMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
    update(cache, result) {
      if (result.data?.updateLastVisitedLesson) {
        const lastVisited = result.data.updateLastVisitedLesson;
        cache.writeFragment({
          id: cache.identify(lastVisited),
          fragment: LastVisitedLessonContentFragmentDoc,
          data: {
            ...lastVisited,
          },
        });
      }
    },
  });

  const [updateLastVisitedUnitStudyReview] =
    useUpdateLastVisitedUnitStudyReviewMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      update(cache, result) {
        if (result.data?.updateLastVisitedUnitStudyReview) {
          const lastVisited = result.data.updateLastVisitedUnitStudyReview;
          cache.writeFragment({
            id: cache.identify(lastVisited),
            fragment: LastVisitedUnitStudyReviewContentFragmentDoc,
            data: {
              ...lastVisited,
            },
          });
        }
      },
    });

  useEffect(() => {
    if (curContent?.id && curContent?.id !== content.id) {
      setPageNumber(1);
    }
    setCurContent(content);
  }, [lastPageNum, curContent?.id, content, setCurContent, setPageNumber]);

  useEffect(() => {
    if (
      !isHomeEDApp &&
      !isPreview &&
      lesson?.id &&
      content.id &&
      !isParentApp &&
      (currentUnit?.id || unit?.id)
    ) {
      const curIndex =
        lesson?.modules?.findIndex((m) => m.id === content.id) || 0;
      const prevModule =
        curIndex >= 1 ? lesson?.modules?.at(curIndex - 1) : undefined;

      updateLastVisitedLesson({
        variables: {
          input: {
            lessonId: lesson.id,
            moduleId: content.id,
            previousModuleId: prevModule?.id,
            unitId: currentUnit?.id || unit?.id || "",
            pageNumber: pageNumber,
            sectionId: section?.id,
          },
        },
      });
    }
  }, [
    program?.id,
    lesson,
    content.id,
    currentUnit?.id,
    updateLastVisitedLesson,
    pageNumber,
    isParentApp,
    unit?.id,
    section?.id,
    isPreview,
  ]);

  useEffect(() => {
    if (
      !isHomeEDApp &&
      !isPreview &&
      isStudyGuide &&
      content.id &&
      !isParentApp &&
      (currentUnit?.id || unit?.id) &&
      currentUnit?.studyGuides
    ) {
      const curIndex =
        currentUnit?.studyGuides.findIndex((m) => m?.id === content.id) || 0;
      const prevModule =
        curIndex >= 1 ? currentUnit?.studyGuides.at(curIndex - 1) : undefined;

      updateLastVisitedUnitStudyReview({
        variables: {
          input: {
            moduleId: content.id,
            previousModuleId: prevModule?.id,
            unitId: currentUnit?.id || unit?.id || "",
            pageNumber: pageNumber,
          },
        },
      });
    }
  }, [
    program?.id,
    content.id,
    currentUnit?.id,
    updateLastVisitedUnitStudyReview,
    pageNumber,
    isParentApp,
    unit?.id,
    isPreview,
    isStudyGuide,
    currentUnit?.studyGuides,
  ]);

  const onNext = useCallback(() => {
    if (hasNextContent && !nextContent?.pauseHere) {
      selectNext(content);
    }
  }, [content, hasNextContent, nextContent?.pauseHere, selectNext]);

  const handlePageNumber = useCallback(
    (value: any) => {
      setPageNumber(value);
    },
    [setPageNumber]
  );

  const handleFullscreen = () => {
    setIsFullscreen((current) => !current);
  };

  const handleStartLesson = useCallback(() => {
    if (startLesson && lesson) startLesson(lesson);
  }, [startLesson, lesson]);

  const presentationContent = useCallback(
    (isFullScreen) => {
      if (
        content &&
        (content.data ||
          content.formData ||
          content.type === ContentType.PARENT)
      ) {
        switch (content.type) {
          case ContentType.VIDEO: {
            return (
              <Video
                data={content.data!}
                key={content.id}
                isFullScreen={isFullScreen}
                metaData={content}
                onNext={onNext}
                isPreview={isPreview}
                goBack={goBack}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.AUDIO: {
            return (
              <Audio
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.PDF: {
            if (isMaterial) {
              return (
                <MaterialPDF
                  needPrint={needPrint}
                  data={content.data!}
                  isFullScreen={isFullScreen}
                  goBack={goBack}
                  goFullscreen={handleFullscreen}
                />
              );
            } else {
              return (
                <Pdf
                  data={content.data!}
                  isFullScreen={isFullScreen}
                  isPreview={isPreview}
                  goBack={goBack}
                />
              );
            }
          }
          case ContentType.SLIDE: {
            return (
              <Slide
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.REVEALSLIDE: {
            return (
              <RevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.ZIPSLIDE: {
            return (
              <ZipRevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.FORM: {
            return (
              <Form
                data={content.data!}
                formData={content.formData}
                isFullScreen={isFullScreen}
                id={content.id}
                name={content.name}
                isPreview={isPreview}
                goBack={goBack}
                studyPlanId={studyPlanId}
                lesson={lesson}
                unit={unit}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
                startLesson={handleStartLesson}
              />
            );
          }
          case ContentType.PARENT: {
            return (
              <Parent
                isFullScreen={isFullScreen}
                skill={currentUnit?.skill!}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.SKILL: {
            return (
              <Skill
                data={content.data!}
                isFullScreen={isFullScreen}
                description={content.description}
                isPreview={isPreview}
                goBack={goBack}
                onExit={onExit}
                isParentApp={isParentApp}
              />
            );
          }
          default: {
            logger.debug(`[Not supported type]: `, content.type);
          }
        }
      } else if (content) {
        return (
          <DefaultContent
            isFullScreen={isFullScreen}
            isPreview={isPreview}
            content={content}
            goBack={goBack}
          />
        );
      }
    },
    [
      content,
      onNext,
      isPreview,
      goBack,
      onExit,
      pageNumber,
      handlePageNumber,
      currentUnit?.skill,
      unit,
      isParentApp,
      studyPlanId,
      lesson,
      isMaterial,
      needPrint,
      handleStartLesson,
    ]
  );

  return (
    <>
      {isPreview ? (
        <div className={styles.wrapper}>
          {showBanner && isHomeEDApp ? (
            <div className={styles.previewBanner}>
              <img src={preview} alt={"preview"} />
            </div>
          ) : null}
          <div
            className={isPreview ? styles.contentWithPreview : styles.content}
            style={{ marginTop: !isHomeEDApp ? 10 : 0 }}
          >
            {presentationContent(false)}
          </div>
        </div>
      ) : (
        <FullScreenWrapper
          lesson={lesson}
          content={content}
          goBack={goBack}
          isMaterial={isMaterial}
          isFullscreen={isFullscreen}
        >
          {presentationContent}
        </FullScreenWrapper>
      )}
    </>
  );
}

export default SelectedContent;
